import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Switch } from "@fluentui/react-components";
import { Target, ChevronDown, X } from "lucide-react";
import CustomPopover from "../popover/Popover";
import CustomButton from "../controls/button/Button";
import GanttViewModeSelect from "../controls/ganttViewModeSelect/GanttViewModeSelect";
import Modal from "../controls/modal/Modal";
import InlineDateRangeFilter from "../controls/inlineDateRangeFilter/InlineDateRangeFilter";
import { Cross, PrintIcon } from "../../modules/default-svg";
import projectService from "../../store/services/projects.service";
import { generalActions } from "../../store/actions";
import styles from "./ReportButton.module.scss";

const REPORT_SECTIONS = [
    { key: "IncludeList", label: "List" },
    { key: "IncludeGantt", label: "Gantt View" },
];

const RED = "#dc2626";
const RED_LIGHT = "#fee2e2";

/**
 * Split pill button for a project timeline: the main half toggles the red critical-task highlight
 * (owned by the parent through `active`/`onToggle`), the caret half opens the standalone Critical
 * Path report options (List / Gantt View and a Start/End range), mirroring the WSR options. Styled
 * like the Key Events pill, in red.
 */
export default function CriticalPathButton({ projectId, active, onToggle }) {
    const [openPopover, setOpenPopover] = useState(false);
    const [sections, setSections] = useState({ IncludeList: true, IncludeGantt: true });
    const [viewMode, setViewMode] = useState(2);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const dispatch = useDispatch();

    const handleGenerate = () => {
        setIsLoading(true);
        projectService
            .criticalPathReportRequest(projectId, {
                IncludeList: sections.IncludeList,
                IncludeGantt: sections.IncludeGantt,
                ViewMode: viewMode,
                StartDate: startDate || null,
                EndDate: endDate || null,
                Tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
            .then((response) => {
                const pdfBlob = new Blob([response], { type: "application/pdf" });
                setReportData({
                    name: "Project - Critical Path Report",
                    pdfData: URL.createObjectURL(pdfBlob),
                });
                setOpenPopover(false);
            })
            .catch((error) => {
                console.error("Critical path report generation failed:", error);
                dispatch(
                    generalActions.showToast({
                        title: "Report generation failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div className="d-flex align-items-center">
            <CustomPopover
                icon={
                    <div
                        className="flex items-center overflow-hidden transition-all"
                        style={{
                            border: `1px solid ${RED}`,
                            borderRadius: "20px",
                            backgroundColor: active ? RED_LIGHT : "transparent",
                            color: RED,
                            fontWeight: active ? 600 : 500,
                            fontFamily: "Figtree, sans-serif",
                            fontSize: "13px",
                            boxShadow: active ? `0 0 0 2px ${RED_LIGHT}` : "none",
                        }}
                    >
                        <div
                            className="flex items-center gap-2 cursor-pointer"
                            style={{ padding: "6px 10px 6px 12px" }}
                            title={active ? "Hide critical path highlight" : "Highlight critical path"}
                            onClick={onToggle}
                        >
                            <Target size={16} strokeWidth={active ? 2.75 : 2} />
                            <span>Critical Path</span>
                        </div>
                        <div
                            className="flex items-center cursor-pointer"
                            style={{ padding: "6px 8px", borderLeft: `1px solid ${RED}` }}
                            title="Critical path report"
                            onClick={() => setOpenPopover((prev) => !prev)}
                        >
                            <ChevronDown size={14} />
                        </div>
                    </div>
                }
                minWidth={320}
                PopSurfaceClass="mt-1"
                status={openPopover}
            >
                <div className="text-[#342c82]">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Critical Path Report</span>
                        <div onClick={() => setOpenPopover(false)} style={{ cursor: "pointer" }}>
                            <Cross />
                        </div>
                    </div>
                    <div className={`${styles.filterPopoverBody} FilterPopoverBody`}>
                        <div className="FilterPopoverBodyScroll">
                            <div className="mb-3">
                                <InlineDateRangeFilter
                                    startDate={startDate}
                                    endDate={endDate}
                                    onStartDateChange={setStartDate}
                                    onEndDateChange={setEndDate}
                                    startLabel="Start"
                                    endLabel="End"
                                />
                            </div>
                            <div className={styles.optionsDivider} />
                            {REPORT_SECTIONS.map((section) => (
                                <div
                                    key={section.key}
                                    className={`d-flex align-items-center justify-content-between ${styles.optionRow}`}
                                >
                                    <span className={styles.optionLabel}>{section.label}</span>
                                    <Switch
                                        checked={sections[section.key]}
                                        onChange={(ev, data) =>
                                            setSections((prev) => ({ ...prev, [section.key]: data.checked }))
                                        }
                                        className="ToggleStyle"
                                    />
                                </div>
                            ))}
                            {sections.IncludeGantt && (
                                <>
                                    <div className={styles.optionsDivider} />
                                    <div className="mb-2">
                                        <GanttViewModeSelect value={viewMode} onChange={setViewMode} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter px-[10px]">
                        <CustomButton
                            btnClass="d_btn d_btn_white d_btn_whiteXsm"
                            label="Cancel"
                            icon={<X size={16} />}
                            clickHandler={() => setOpenPopover(false)}
                        />
                        <CustomButton
                            btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                            label="Generate"
                            icon={<PrintIcon color="#fff" />}
                            clickHandler={handleGenerate}
                            showLoading={isLoading}
                            isDisabled={!sections.IncludeList && !sections.IncludeGantt}
                        />
                    </div>
                </div>
            </CustomPopover>
            {reportData && (
                <Modal
                    title={reportData.name}
                    handleCancel={() => setReportData(null)}
                    body={
                        <iframe
                            title="PDF Viewer"
                            src={reportData.pdfData}
                            width="100%"
                            height="100%"
                        ></iframe>
                    }
                    type={true}
                    size=""
                />
            )}
        </div>
    );
}
